import { useRef, useState } from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import sanitizeTagCategories from 'helpers/sanitizeTagCategories'

import ActionContainer from 'js/components/ActionContainer/ActionContainer'
import DeleteModal from 'js/components/EntityActions/DeleteModal'
import Errors from 'js/components/Errors'
import FormItem from 'js/components/Form/FormItem'
import { Container } from 'js/components/Grid'
import TabMenu from 'js/components/Navigation/TabMenu'
import TabPanel from 'js/components/Navigation/TabPanel'
import Sticky from 'js/components/Sticky/Sticky'
import { useMutationWithRefetch } from 'js/hooks/useMutationWithRefetch'
import { MAX_LENGTH } from 'js/lib/constants'
import { featuredEditFragment } from 'js/lib/fragments/featured'
import { iconViewFragment } from 'js/lib/fragments/icon'

import styles from './AddEditForm.module.css'
import GeneralTab from './GeneralTab/GeneralTab'
import NavigationTab from './NavigationTab/NavigationTab'
import SettingsTab from './SettingsTab/SettingsTab'

// BE only needs the guid and the value of a profile field
function formatProfileFields(profileFields) {
    return profileFields.map(({ field, value }) => ({
        guid: field.guid,
        value,
    }))
}

function getAutoMembershipFields(entity, site) {
    return (
        // Groups with selected auto membership fields
        entity?.autoMembershipFields ||
        // New groups or groups without selected auto membership fields
        site?.autoMembershipProfileFields?.map((autoMembershipProfileField) => {
            return {
                field: autoMembershipProfileField,
                value: [],
            }
        }) ||
        []
    )
}

const AddEditForm = ({ title, entity, groupPages, viewer, site }) => {
    const navigate = useNavigate()
    const location = useLocation()

    const refFeatured = useRef<any>(null)

    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const toggleDeleteModal = () => setShowDeleteModal(!showDeleteModal)

    const [tab, setTab] = useState('general')

    const [mutationErrors, setErrors] = useState()

    const { t } = useTranslation()

    const handleClose = () => {
        navigate(
            location?.state?.prevPathname ||
                (entity && entity.url) ||
                '/groups',
            { replace: true },
        )
    }

    const afterDelete = () => {
        navigate('/groups', { replace: true })
    }

    const [categoryTags, setCategoryTags] = useState(
        entity?.tagCategories || [],
    )
    const [defaultTagCategories, setDefaultTagCategories] = useState(
        entity?.defaultTagCategories || [],
    )

    const [customTags, setCustomTags] = useState(new Set(entity?.tags || []))

    const [defaultTags, setDefaultTags] = useState(
        new Set(entity?.defaultTags || []),
    )

    const [isMembershipOnRequest, setIsMembershipOnRequest] = useState(
        entity?.isMembershipOnRequest || false,
    )
    const [isClosed, setIsClosed] = useState(entity?.isClosed || false)

    const [plugins, setPlugins] = useState(entity?.plugins || [])

    const [menu, setMenu] = useState(entity?.menu || [])

    const [addMutation] = useMutationWithRefetch(ADD_GROUP, {
        refetchFields: ['groups'],
    })
    const [editMutation] = useMutation<any>(EDIT_GROUP)

    const onSubmit = async ({
        name,
        icon,
        richDescription,
        introduction,
        isIntroductionPublic,
        welcomeMessage,
        autoNotification,
        fileNotification,
        isFeatured,
        isAutoMembershipEnabled,
        isLeavingGroupDisabled,
        isHidden,
        isCollaboraEnabled,
        showDefaultTags,
        isJoinButtonVisible,
        isChatEnabled,
        startPage,
        autoMembershipFields,
        isMenuAlwaysVisible,
        showMemberProfileFieldGuids,
        requiredProfileFieldGuids,
        requiredProfileFieldsMessage,
        vcardFieldGuids,
    }) => {
        const getMenuObject = (item) => {
            if (typeof item === 'string') {
                return null
            } // Page type added, but no page selected
            const { label, plugin, page, type, access, submenu } = item
            const id = plugin || page?.guid || item.id || item.link
            return {
                type,
                label: type === 'plugin' ? undefined : label,
                access,
                ...(type === 'submenu'
                    ? { submenu: submenu.map(getMenuObject) }
                    : {
                          id,
                      }),
            }
        }

        const input = {
            ...(entity ? { guid: entity.guid } : {}),
            name,
            featured: refFeatured.current && refFeatured.current.getValue(),
            iconGuid: icon?.guid || null,
            richDescription,
            introduction,
            isIntroductionPublic,
            welcomeMessage,
            plugins,
            showMemberProfileFieldGuids,
            requiredProfileFieldGuids,
            requiredProfileFieldsMessage,
            vcardFieldGuids,
            autoNotification,
            fileNotification,
            isFeatured,
            isAutoMembershipEnabled,
            isLeavingGroupDisabled,
            isHidden,
            isCollaboraEnabled,
            tagCategories: sanitizeTagCategories(
                site.tagCategories,
                categoryTags,
            ),
            tags: Array.from(customTags),
            isMembershipOnRequest,
            isClosed,
            defaultTagCategories: showDefaultTags
                ? sanitizeTagCategories(
                      site.tagCategories,
                      defaultTagCategories,
                  )
                : null,
            defaultTags: showDefaultTags ? Array.from(defaultTags) : null,
            isChatEnabled,
            ...(entity && { startPageGuid: startPage || '' }), // We cannot set a start page as long as the group does not exist
            menu: menu.map(getMenuObject).filter(Boolean),
            isJoinButtonVisible,
            autoMembershipFields: formatProfileFields(autoMembershipFields),
            isMenuAlwaysVisible,
        }

        setErrors(null)

        const mutate = entity ? editMutation : addMutation

        if (typeof mutate === 'function') {
            await mutate({
                variables: {
                    input,
                },
            })
                .then(({ data }) => {
                    if (entity) {
                        navigate(
                            location?.state?.prevPathname ||
                                data.editGroup.group.url,
                            {
                                replace: true,
                                state: { reload: true },
                            },
                        )
                    } else {
                        navigate(data.addGroup.group.url, { replace: true })
                    }
                })
                .catch((errors) => {
                    console.error(errors)
                    setErrors(errors)
                })
        }
    }

    const defaultValues = {
        name: entity?.name || null,
        icon: entity?.icon || null,
        richDescription: entity?.richDescription || null,
        introduction: entity?.introduction || null,
        isIntroductionPublic: entity?.isIntroductionPublic || false,
        welcomeMessage: entity?.welcomeMessage || null,
        autoNotification: entity ? entity.autoNotification : true,
        fileNotification: entity ? entity.fileNotification : false,
        isFeatured: entity?.isFeatured || false,
        isJoinButtonVisible: entity?.isJoinButtonVisible ?? true,
        isAutoMembershipEnabled: entity?.isAutoMembershipEnabled || false,
        isLeavingGroupDisabled: entity?.isLeavingGroupDisabled || false,
        isHidden: entity?.isHidden || false,
        isCollaboraEnabled: entity?.isCollaboraEnabled || false,
        requiredProfileFieldsMessage:
            entity?.requiredProfileFieldsMessage || '',
        showDefaultTags:
            entity?.defaultTags?.length > 0 ||
            entity?.defaultTagCategories?.length > 0,
        isChatEnabled: entity?.isChatEnabled || false,
        ...(entity && { startPage: entity?.startPage?.guid || '' }), // We cannot set a start page as long as the group does not exist
        autoMembershipFields: getAutoMembershipFields(entity, site),
        isMenuAlwaysVisible: entity?.isMenuAlwaysVisible || true,
        showMemberProfileFieldGuids: entity?.showMemberProfileFieldGuids || [],
        requiredProfileFieldGuids: entity?.requiredProfileFieldGuids || [],
        vcardFieldGuids: entity?.vcardFieldGuids || [],
    }

    const formMethods = useForm({
        mode: 'onChange',
        defaultValues,
    })

    const {
        control,
        handleSubmit,
        formState: { errors, isValid, isSubmitting },
    } = formMethods

    const autoMemberShipFieldArray = useFieldArray({
        control,
        name: 'autoMembershipFields',
    })

    const onError = (errors: any) => {
        return new Promise((_resolve, reject) => {
            reject(new Error(errors))
        })
    }

    const submitForm = handleSubmit(onSubmit, onError)

    return (
        <ActionContainer
            title={title}
            publish={submitForm}
            onDelete={entity ? toggleDeleteModal : null}
            onClose={handleClose}
            isValid={isValid}
            isPublished={!!entity}
            isPublishing={isSubmitting}
        >
            <FormProvider {...formMethods}>
                <form>
                    <Container size="small">
                        <FormItem
                            control={control}
                            type="text"
                            name="name"
                            label={t('global.name')}
                            required
                            maxLength={MAX_LENGTH.groupName}
                            errors={errors}
                            size="large"
                            style={{ marginBottom: 16 }}
                            testid="field-name"
                        />
                        <Errors errors={mutationErrors} />
                    </Container>

                    <Sticky className={styles.tabMenuWrapper}>
                        <Container size="small">
                            <TabMenu
                                label={t('entity-group.group-options')}
                                options={[
                                    {
                                        onClick: () => setTab('general'),
                                        label: t('global.general'),
                                        isActive: tab === 'general',
                                        id: 'tab-general',
                                        ariaControls: 'tabpanel-general',
                                    },
                                    {
                                        onClick: () => setTab('settings'),
                                        label: t('global.settings'),
                                        isActive: tab === 'settings',
                                        id: 'tab-settings',
                                        ariaControls: 'tabpanel-settings',
                                    },
                                    {
                                        onClick: () => setTab('navigation'),
                                        label: t('global.navigation'),
                                        isActive: tab === 'navigation',
                                        id: 'tab-navigation',
                                        ariaControls: 'tabpanel-navigation',
                                    },
                                ]}
                                edgePadding
                                edgeMargin
                                showBorder
                                style={{ position: 'static' }}
                            />
                        </Container>
                    </Sticky>

                    <TabPanel
                        id={`tabpanel-general`}
                        aria-labelledby={`tab-general`}
                        visible={tab === 'general'}
                    >
                        <GeneralTab
                            ref={refFeatured}
                            site={site}
                            entity={entity}
                            tags={categoryTags}
                            setTags={setCategoryTags}
                            customTags={customTags}
                            setCustomTags={setCustomTags}
                            isMembershipOnRequest={isMembershipOnRequest}
                            setIsMembershipOnRequest={setIsMembershipOnRequest}
                            isClosed={isClosed}
                            setIsClosed={setIsClosed}
                        />
                    </TabPanel>

                    <TabPanel
                        id={`tabpanel-settings`}
                        aria-labelledby={`tab-settings`}
                        visible={tab === 'settings'}
                    >
                        <SettingsTab
                            site={site}
                            viewer={viewer}
                            autoMemberShipFieldArray={autoMemberShipFieldArray}
                            // @ts-expect-error TODO: is this component wrong or its implementation?
                            entity={entity}
                            plugins={plugins}
                            setPlugins={setPlugins}
                            isAutoMembershipEnabled={
                                defaultValues.isAutoMembershipEnabled
                            }
                            defaultTags={defaultTags}
                            setDefaultTags={setDefaultTags}
                            defaultTagCategories={defaultTagCategories}
                            setDefaultTagCategories={setDefaultTagCategories}
                            menu={menu}
                            setMenu={setMenu}
                        />
                    </TabPanel>

                    <TabPanel
                        id={`tabpanel-navigation`}
                        aria-labelledby={`tab-navigation`}
                        visible={tab === 'navigation'}
                    >
                        <NavigationTab
                            entity={entity}
                            groupPages={groupPages?.edges || []}
                            menu={menu}
                            setMenu={setMenu}
                        />
                    </TabPanel>
                </form>
            </FormProvider>

            <DeleteModal
                isVisible={showDeleteModal}
                onClose={toggleDeleteModal}
                title={t('entity-group.delete')}
                entity={entity}
                afterDelete={afterDelete}
                refetchFields={['groups']}
            />
        </ActionContainer>
    )
}

const ADD_GROUP = gql`
    mutation addGroup($input: addGroupInput!) {
        addGroup(input: $input) {
            group {
                guid
                url
            }
        }
    }
`

const EDIT_GROUP = gql`
    mutation editGroup($input: editGroupInput!) {
        editGroup(input: $input) {
            group {
                guid
                name
                url
                description
                richDescription
                introduction
                welcomeMessage
                autoNotification
                fileNotification
                plugins
                ${iconViewFragment}
                isClosed
                isMembershipOnRequest
                tags
                tagCategories {
                    name
                    values
                }
                chat {
                    guid
                    unread
                }
                isChatEnabled
                isLeavingGroupDisabled
                isJoinButtonVisible
                isHidden
                isAutoMembershipEnabled
                isIntroductionPublic
                showMemberProfileFieldGuids
                requiredProfileFieldGuids
                requiredProfileFieldsMessage
                vcardFieldGuids
                ${featuredEditFragment}
                defaultReadAccessId
                isCollaboraEnabled
            }
        }
    }
`

export default AddEditForm
